export interface PhaseShiftInput {
  injectionAngle: string | number;
  degreeAngle: string | number;
  faultInducedCylinder: string;
  detectedCylinder: string;
  firingOrder: string;
  engineStroke: "2" | "4" | number;
}

export function calculateNewPhaseShift({
  injectionAngle,
  degreeAngle,
  faultInducedCylinder,
  detectedCylinder,
  firingOrder,
  engineStroke,
}: PhaseShiftInput): { ok: boolean; value?: number; error?: string } {
  const order = firingOrder
    .split(/[\s,]+/)
    .map((value) => value.trim())
    .filter(Boolean);

  const n = order.length;
  const currentAngle = Number(degreeAngle);
  const normalizedInjectionAngle =
    injectionAngle === "" || injectionAngle === null || injectionAngle === undefined
      ? 0
      : Number(injectionAngle);

  if (!n) {
    return { ok: false, error: "Enter a valid firing order." };
  }

  if (Number.isNaN(currentAngle)) {
    return { ok: false, error: "Enter a valid current phase angle." };
  }

  const induced = faultInducedCylinder.trim();
  if (!induced) {
    return { ok: false, error: "Select or enter the fault-induced cylinder." };
  }

  const inducedIndex = order.findIndex(
    (value) => value.toUpperCase() === induced.toUpperCase(),
  );

  if (inducedIndex === -1) {
    return {
      ok: false,
      error: "Enter a valid fault cylinder from the firing order.",
    };
  }

  const hasInjectionAngle =
    injectionAngle !== "" && injectionAngle !== null && injectionAngle !== undefined;
  const hasDetectedCylinder = !!String(detectedCylinder || "").trim();

  if (!hasInjectionAngle && !hasDetectedCylinder) {
    return {
      ok: false,
      error: "Provide either the injection angle or the detected cylinder.",
    };
  }

  const detected = String(detectedCylinder || induced).trim();
  const detectedIndex = order.findIndex(
    (value) => value.toUpperCase() === detected.toUpperCase(),
  );

  if (detectedIndex === -1) {
    return {
      ok: false,
      error: "Enter a valid fault cylinder from the firing order.",
    };
  }

  if (Number.isNaN(normalizedInjectionAngle)) {
    return { ok: false, error: "Enter a valid injection angle." };
  }

  const anglePerPosition = (engineStroke === "4" ? 720 : 360) / n;
  const value =
    normalizedInjectionAngle + (inducedIndex - detectedIndex) * anglePerPosition - currentAngle;

  return { ok: true, value };
}
